import type { BrandConfig, NavEntry } from '../types';
import type { ScreenId } from '@/app/navigation/types';
import { BASE_NAV, BASE_SCREENS } from '../base';

// TelecomClub Partners — третий вариант кабинета на том же бекенде, по схеме
// wapclick: состав урезан, вид переложен. Ориентир — макет из Claude Design
// (разделы 2–5), собранный без дизайнера: вид и раскладку берём оттуда, данные —
// только из существующих ручек. Экраны перекладываются по одному, поэтому всё,
// что ниже помечено «как у serious», — заглушка до своего экрана, а не решение.

// Состав — по меню макета. Статистика одна (без журналов подписок, транзакций и
// трафбэка), без лендингов, ТДС, рефералов, FAQ и правил.
//
// 2FA выключен: в макете переключатель живёт в карточке «Безопасность», но на
// экран настроек его решили пока не ставить (24.09), а другой путь к экрану
// есть только из дропдауна шапки serious (header === 'top'), которой у бренда нет.
const HIDDEN_SCREENS: ReadonlyArray<ScreenId> = [
  'stat.trafficback',
  'subscriptions',
  'transactions',
  'landings',
  'tds',
  'referals',
  'faq',
  'rules',
  'settings.2fa',
];

// Порядок меню макета: «Потоки» стоят перед «Источниками» (в базе наоборот).
// Пункты берутся из базы целиком — иконки и бейджи общие, меняется только
// порядок; пункты выключенных экранов отпадают сами, а группа «Статистика» с
// одним живым пунктом схлопывается в ссылку.
const NAV_ORDER: ReadonlyArray<ScreenId> = [
  'stat.general',
  'streams',
  'sources',
  'payments',
  'news',
  'settings',
  'support',
];

const navTarget = (entry: NavEntry): ScreenId => (entry.kind === 'link' ? entry.screen : entry.primary);

// «Потоки» и «IP-адреса» — самостоятельными пунктами, а не подпунктами группы
// «Потоки» (правка начальства 25.09): группа из базы раскрывается в две ссылки,
// переключатель-табы ниже lg (compactTabs) уходит вместе с ней.
const STREAM_LINKS: NavEntry[] = [
  { kind: 'link', screen: 'streams', icon: 'Arrow_Left_Right', labelKey: 'streams' },
  { kind: 'link', screen: 'iprates', icon: 'Network' },
];

const NAV: NavEntry[] = NAV_ORDER.flatMap((screen) =>
  screen === 'streams' ? STREAM_LINKS : BASE_NAV.filter((entry) => navTarget(entry) === screen),
);

export const brandConfig: BrandConfig = {
  id: 'telecom',
  // Дашборда нет (правка начальства 25.09): ни в меню, ни в роутере — код экрана и
  // вид layout.dashboard остаются. Стартовый экран — первый пункт меню, статистика.
  screens: BASE_SCREENS.filter((id) => !HIDDEN_SCREENS.includes(id)),
  nav: NAV,
  columns: {
    // Колонки — по макету: без «Отписок в первый день», ИК и жалоб (тот же
    // набор, что у wapclick). С ними таблица на 1280 уходила в горизонтальный
    // скролл.
    generalStat: {
      exclude: ['scope_offs', 'ic', 'complains'],
    },
  },
  filters: {
    generalStat: {},
  },
  stream: {},
  layout: {
    // Каркас макета: логотип в винном сайдбаре слева, язык, баланс и аватар —
    // строкой над контентом справа, меню пилюлями без иконок.
    header: 'in-content',
    sidebar: 'left',
    // Профиль — аватар с инициалами в строке аккаунта, в меню его нет.
    sidebarProfile: 'none',
    languageSwitcher: 'select',
    sidebarMenu: 'pills',
    // Планшет — рейка иконок, телефон — верхняя панель и меню на весь экран.
    compactNav: 'rail-topbar',
    // Над заголовком страницы — строка о том, что в разделе.
    pageTitle: 'with-subtitle',
    // Вход и регистрация — белая карточка на красной волне.
    auth: 'card',
    news: 'rows',
    // Тикеты — список и переписка рядом, ниже lg — два экрана.
    support: 'split',
    // Настройки — карточки в две колонки, у каждой своё сохранение.
    settings: 'columns',
    // Финансы — кошелёк и курсы сверху, заказ с подтверждением, история.
    payments: 'two-step',
    // Источники — таблица, на планшете в три колонки, на телефоне строками.
    sources: 'adaptive',
    // Статистика — фильтры, график и таблица отдельными карточками, фильтры
    // видны всегда. statFilters у вида 'cards' задаёт только раскладку рядов
    // (6 + 4, как в макете): кнопки, которая прятала бы панель, у него нет.
    stat: 'cards',
    statFilters: 'collapsible',
    // Группировка — обычным списком, как остальные селекты (в макете select).
    groupBySelect: 'list',
    // Пагинация — число строк пилюлями, текущая страница кружком, счётчик строк.
    pagination: 'pills',
    // Потоки — таблицей с xl, карточками ниже, легенда флагов под списком.
    streamsList: 'table',
    globalLinks: 'settings',
    // Промо-ссылка — содержание классического вида в оформлении макета.
    promoLink: 'boxed',
    // Форма потока — карточками, лендинги с вкладками режима и деревом операторов.
    streamForm: 'cards',
    // IP-адреса — страна за раз: список стран слева, карточка страны справа.
    ipRates: 'countries',
    // Дальше — как у serious, до перекладки своего экрана.
    referals: 'classic',
    ticketForm: 'type-first',
    datePicker: 'dropdown',
    // На телефоне модалки — шторки снизу.
    modal: 'sheet',
    // Дашборд — четыре KPI, заработок с периодом и валютой, лендинги табами.
    dashboard: 'kpi',
  },
  features: {
    // Колокольчика в макете нет. Бейджи новостей и тикетов на пунктах меню
    // остаются.
    notifications: false,
    statChart: true,
    // Внешний PWA-сервис живёт на поддомене Serious.
    pwaHandoff: false,
    // Письмо активации уходит только при ID_PROJECT=private (isProjectPrivate()),
    // у этого инстанса его не будет — как у wapclick.
    emailActivation: false,
  },
};

export default brandConfig;
